import { defineStore } from "pinia"
import type { RouteLocationNormalized } from "vue-router"

import type { MenuItem } from "~/types/MenuItem"

export const useMainMenuStore = defineStore("mainMenuStore", {
  state: () => ({
    selectedItemValues: ["dashboard"],
    items: [
      {
        value: "dashboard",
        title: "Sākums",
        url: "/",
        icon: "fa-house",
        categories: [],
        alwaysVisible: true,
        public: true,
      },
      {
        value: "overview-and-journals",
        title: "Pārskats un žurnāli",
        icon: "fa-book",
        categories: [
          {
            value: "overview",
            title: "Pārskats",
            url: "/parskats",
            icon: "fa-th-large",
            alwaysVisible: true,
          },
          {
            value: "journals",
            title: "Žurnāli",
            url: "/mani-zurnali",
            icon: "fa-book",
            public: false,
            alwaysVisible: false,
            teacherWithLessons: true,
            studentWithLessons: true,
          },
          {
            value: "result-insights",
            title: "Rezultātu analīze",
            url: "/rezultatu-analize",
            icon: "fa-light fa-chart-mixed",
            public: false,
            alwaysVisible: false,
            teacherWithLessons: true,
          },
        ],
        alwaysVisible: true,
      },
      {
        value: "smart-files",
        title: "Viedgrāmatas",
        icon: "fa-book-open",
        alwaysVisible: true,
        public: false,
        // plans: ["plus", "pro"],
        categories: [
          {
            value: "subjects",
            title: "Mācību priekšmeti",
            url: "/viedtemas",
            icon: "fa-pencil",
            alwaysVisible: true,
          },
          {
            value: "results",
            title: "Rezultātu atskaite",
            url: "/atskaite",
            icon: "fa-pie-chart",
            alwaysVisible: false,
            oldUrl: "rezultatu-atskaite",
            teacherWithLessons: true,
          },
          {
            value: "progressmeter",
            title: "Progresometrs",
            url: "/progress",
            icon: "fa-line-chart",
            alwaysVisible: false,
            progressometer: true,
            // plans: ["plus"],
            oldUrl: "progresometrs",
          },
        ],
      },
      {
        value: "tests",
        title: "Testi",
        url: "/testi",
        icon: "fa-cubes",
        categories: [],
        alwaysVisible: false,
      },
      {
        value: "tests-and-questions-teachers",
        title: "Testi un jautājumi",
        icon: "fa-cubes",
        alwaysVisible: false,
        categories: [
          {
            value: "question-bank",
            title: "Jautājumu banka",
            url: "/jautajumu-banka",
            icon: "fa-diagram-subtask",
          },
          {
            value: "my-tests",
            title: "Mani testi",
            url: "/mani-testi",
            icon: "fa-person-chalkboard",
          },
          {
            value: "soma-tests",
            title: "Somas testi",
            url: "/somas-testi",
            icon: "fa-panel-ews",
          },
        ],
      },
      {
        value: "e-books",
        title: "E-grāmatas",
        url: "/e-gramatas",
        icon: "fa-book-atlas",
        categories: [],
        alwaysVisible: true,
        public: true,
      },
      {
        value: "worksheet",
        title: "Darba lapas",
        url: "/darba-lapas",
        icon: "fa-file-text",
        categories: [],
        alwaysVisible: true,
        // plans: ["plus", "pro"],
      },
      {
        value: "video",
        title: "Video",
        url: "/video",
        icon: "fa-video-camera",
        categories: [],
        alwaysVisible: true,
        // plans: ["plus", "pro"],
      },
      {
        value: "methodical-supplies",
        title: "Metodiskie līdzekļi",
        url: "/metodiskie-lidzekli",
        icon: "fa-file-user",
        categories: [],
        alwaysVisible: false,
        // plans: ["pro"],
      },
      {
        value: "test-works",
        title: "Pārbaudes darbi",
        info: "Tikai skolotājiem",
        url: "/parbaudes-darbi",
        icon: "fa-pen-nib",
        categories: [],
        alwaysVisible: false,
        // plans: ["pro"],
      },
      {
        value: "active-inspire",
        title: "ActivInspire",
        url: "/interaktivie-materiali",
        icon: "fa-presentation-screen",
        categories: [],
        alwaysVisible: false,
        activeInspire: true,
      },
      {
        value: "e-courses",
        title: "E-kursi pedagogiem",
        url: "/e-courses",
        icon: "fa-puzzle-piece",
        categories: [],
        public: false,
        alwaysVisible: false,
        oldUrl: "e-kursi",
        coursesAllowed: true,
      },
      {
        value: "e-courses",
        title: "E-kursi pedagogiem",
        url: "/e-courses",
        icon: "fa-puzzle-piece",
        categories: [],
        public: false,
        alwaysVisible: false,
        oldUrl: "e-kursi",
        coursesUserActiveGroup: true,
      },
      {
        value: "my-products",
        title: "Bibliotēka",
        url: "/my-products",
        icon: "fa-books",
        categories: [],
        alwaysVisible: false,
        public: false,
        oldUrl: "my-products",
        hasActivatedProducts: true,
      },
      {
        value: "all-resources",
        title: "Visi resursi",
        url: "/visi-resursi",
        icon: "fa-chart-tree-map",
        categories: [],
        alwaysVisible: true,
      },
    ] as MenuItem[],
  }),

  actions: {
    // TODO: Implement use of route names insdead of manually going through urls
    selectActiveItemValue(route: RouteLocationNormalized) {
      const values = [] as string[]
      const firstURLLocation = "/" + route.path.split("/")[1]

      this.items.forEach((item) => {
        if (item.url === firstURLLocation) {
          values.push(item.value)
        }
        item.categories.forEach((subItem) => {
          if (item.url === firstURLLocation) {
            values.push(item.value)
            values.push(subItem.value)
          }
        })
      })

      if (values.length === 0) {
        this.items.forEach((item) => {
          if (item.url && item.url !== "/" && item.url.match(firstURLLocation)) {
            values.push(item.value)
          }
          item.categories.forEach((subItem) => {
            if (subItem.url && subItem.url !== "/") {
              if (subItem.url.split("/")[1] === route.path.split("/")[1]) {
                values.push(item.value)
                values.push(subItem.value)
              }
            }
          })
        })
      }
      this.selectedItemValues = values
    },

    isSelected(value: string) {
      return this.selectedItemValues.includes(value)
    },
  },
})
